import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, formatApiErrorDetail } from "../lib/api";
import { Heart, ArrowRight, Sparkles, X } from "lucide-react";
import { toast } from "sonner";

function formatZAR(n) {
  return new Intl.NumberFormat("en-ZA", { style: "currency", currency: "ZAR", maximumFractionDigits: 0 }).format(n);
}

export default function SponsorSection() {
  const [packages, setPackages] = useState([]);
  const [impact, setImpact] = useState(null);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ donor_name: "", donor_email: "" });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    api.get("/sponsor/packages").then((r) => setPackages(r.data)).catch(() => {});
    api.get("/sponsor/impact").then((r) => setImpact(r.data)).catch(() => {});
  }, []);

  const onCheckout = async () => {
    if (!selected) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/sponsor/checkout", {
        package_id: selected.package_id,
        origin_url: window.location.origin,
        donor_name: form.donor_name.trim() || undefined,
        donor_email: form.donor_email.trim() || undefined,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not start checkout");
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start checkout");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="sponsor" className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 py-16">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
        <div className="lg:col-span-5">
          <div className="overline mb-3">Sponsor a session</div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-[#3D405B] tracking-tight leading-tight">
            One sponsor. One session. One young person heard.
          </h2>
          <p className="text-[#5F627D] mt-5 leading-relaxed">
            Every counselling session at Hellofoundation Youth Mental Wellness & Support is free for the young person. They stay free because South Africans like you sponsor them — a single donation funds a full session, in full, for someone who needs it.
          </p>

          {impact && (
            <div className="mt-7 grid grid-cols-2 gap-3" data-testid="sponsor-impact">
              <div className="brand-card p-5">
                <div className="text-3xl font-extrabold text-primary">{impact.sessions_delivered_total}</div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#7A7D96] font-bold mt-1">Sessions delivered</div>
              </div>
              <div className="brand-card p-5">
                <div className="text-3xl font-extrabold text-secondary">{formatZAR(impact.donations_total_zar)}</div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#7A7D96] font-bold mt-1">Raised by sponsors</div>
              </div>
              <div className="brand-card p-5 col-span-2 bg-gradient-to-br from-accent/20 to-secondary/10">
                <div className="flex items-center gap-2 text-[#3D405B]">
                  <Sparkles size={16} className="text-primary" />
                  <span className="font-bold">{impact.sessions_funded_month} sessions funded this month</span>
                </div>
                <div className="text-sm text-[#5F627D] mt-1">{impact.sessions_delivered_month} delivered · {formatZAR(impact.donations_month_zar)} raised</div>
              </div>
            </div>
          )}
        </div>

        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-3 gap-4">
          {packages.map((p) => (
            <button
              key={p.package_id}
              onClick={() => setSelected(p)}
              className="brand-card brand-card-hover p-6 text-left flex flex-col"
              data-testid={`sponsor-pkg-${p.package_id}`}
            >
              <div className="w-11 h-11 rounded-2xl bg-primary/15 text-primary flex items-center justify-center mb-3">
                <Heart size={20} strokeWidth={2.4} />
              </div>
              <div className="font-extrabold text-[#3D405B] text-lg leading-tight">{p.label}</div>
              <div className="mt-3 flex items-baseline gap-1">
                <span className="text-3xl font-extrabold text-primary">{formatZAR(p.amount)}</span>
              </div>
              <p className="text-sm text-[#5F627D] mt-3 flex-1">{p.blurb}</p>
              <div className="mt-4 inline-flex items-center gap-1.5 text-primary font-bold text-sm">
                Sponsor <ArrowRight size={14} />
              </div>
            </button>
          ))}
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 z-50 bg-[#3D405B]/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => !submitting && setSelected(null)}>
          <div className="bg-card rounded-3xl w-full max-w-md p-7" onClick={(e) => e.stopPropagation()} data-testid="sponsor-modal">
            <div className="flex items-start justify-between mb-4">
              <div>
                <div className="overline">{selected.label}</div>
                <div className="text-3xl font-extrabold text-[#3D405B] mt-1">{formatZAR(selected.amount)}</div>
              </div>
              <button onClick={() => setSelected(null)} className="p-2 rounded-full hover:bg-[#3D405B]/5" disabled={submitting}><X size={18} /></button>
            </div>
            <p className="text-sm text-[#5F627D] mb-5">{selected.blurb}</p>
            <div className="space-y-3 mb-5">
              <input
                value={form.donor_name}
                onChange={(e) => setForm({ ...form, donor_name: e.target.value })}
                placeholder="Your name (optional)"
                className="w-full px-4 py-3 rounded-2xl bg-white border border-[#3D405B]/15 focus:ring-2 focus:ring-primary/40 focus:border-primary outline-none"
                data-testid="sponsor-donor-name"
              />
              <input
                type="email"
                value={form.donor_email}
                onChange={(e) => setForm({ ...form, donor_email: e.target.value })}
                placeholder="Your email (optional, for receipt)"
                className="w-full px-4 py-3 rounded-2xl bg-white border border-[#3D405B]/15 focus:ring-2 focus:ring-primary/40 focus:border-primary outline-none"
                data-testid="sponsor-donor-email"
              />
            </div>
            <button
              onClick={onCheckout}
              disabled={submitting}
              className="w-full px-5 py-3.5 rounded-full bg-primary text-white font-bold shadow-md hover:-translate-y-0.5 hover:shadow-lg transition-all disabled:opacity-60"
              data-testid="sponsor-checkout-btn"
            >
              {submitting ? "Redirecting to Yoco…" : `Sponsor ${formatZAR(selected.amount)}`}
            </button>
            <p className="text-xs text-center text-[#7A7D96] mt-3">
              Secure checkout via Yoco · You&apos;ll be redirected · <Link to="/login" className="text-primary hover:underline">Sign in?</Link>
            </p>
          </div>
        </div>
      )}
    </section>
  );
}
